import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Eye, Bone, Activity, Heart, Wind, Award, ArrowRight, Lock } from "lucide-react";
import KennelSEO, { localBusinessSchema, breadcrumbSchema, faqSchema } from "../kennel/KennelSEO";

const BASE = "https://anatomia.wolfevomkatzenberg.pl";

const LAYERS = [
  { id: "external", name: "Budowa zewnętrzna", desc: "Głowa, szyja, kłąb, grzbiet, zad, klatka piersiowa, kończyny, ogon, szata", icon: Eye, href: "/anatomia/owczarek-niemiecki/budowa-zewnetrzna", ready: true, image: "/german_shepherd_anatomy.png" },
  { id: "skeletal", name: "Układ kostny", desc: "Czaszka, kręgosłup, miednica, kości kończyn, stawy i połączenia", icon: Bone, href: "/anatomia/owczarek-niemiecki/uklad-kostny", ready: true, image: "/anatomy_skeletal.png" },
  { id: "muscular", name: "Układ mięśniowy", desc: "Mięśnie szkieletowe, mięśnie kończyn, napęd i stabilizacja", icon: Activity, href: "/anatomia/owczarek-niemiecki/uklad-miesniowy", ready: false, image: "/anatomy_muscular.png" },
  { id: "organs", name: "Narządy wewnętrzne", desc: "Układ pokarmowy, oddechowy, krążenia, nerwowy i hormonalny", icon: Heart, href: "/anatomia/owczarek-niemiecki/narzady-wewnetrzne", ready: false, image: "/anatomy_organs.png" },
  { id: "movement", name: "Ruch i biomechanika", desc: "Krok, kłus, galop, przenoszenie napędu, znaczenie górnej linii", icon: Wind, href: "/anatomia/owczarek-niemiecki/ruch-i-biomechanika", ready: false, image: "/anatomy_movement.png" },
  { id: "fci", name: "Standard FCI", desc: "Cechy pożądane, proporcje, kryteria oceny sędziowskiej", icon: Award, href: "/anatomia/owczarek-niemiecki/standard-fci", ready: false, image: "/german_shepherd_anatomy.png" },
];

const hubFaq = [
  { q: "Czym jest atlas anatomii owczarka niemieckiego?", a: "To interaktywny przewodnik po budowie owczarka niemieckiego, obejmujący budowę zewnętrzną, układ kostny, mięśniowy, narządy wewnętrzne, biomechanikę ruchu i standard FCI. Każda warstwa pozwala kliknąć konkretną część ciała i poznać jej funkcję, znaczenie dla rasy i kryteria oceny." },
  { q: "Dla kogo jest ten atlas?", a: "Dla każdego zainteresowanego rasą: przyszłych właścicieli, hodowców początkujących i zaawansowanych, miłośników wystaw, studentów kynologii i weterynarii. Treści są podzielone na 3 poziomy — od prostych opisów po szczegółowe kryteria hodowlane." },
  { q: "Jak wygląda prawidłowa budowa owczarka niemieckiego?", a: "Owczarek niemiecki to pies średniej wielkości o lekko wydłużonej sylwetce (110–117%). Powinien mieć wysoki kłąb, prosty grzbiet lekko opadający ku zadowi, głęboką klatkę piersiową i dobrze kątowane kończyny. Wysokość w kłębie: 60–65 cm (psy), 55–60 cm (suki)." },
  { q: "Dlaczego budowa psa ma znaczenie?", a: "Prawidłowa budowa wpływa na zdrowie (mniejsze ryzyko dysplazji), ruch (wydajny kłus dalekiego zasięgu), pracę roboczą i ocenę wystawową. Znając prawidłową budowę, łatwiej ocenisz jakość hodowli i świadomie wybierzesz szczenię." },
  { q: "Co to jest kątowanie kończyn?", a: "Kątowanie to kąty tworzone przez kości w stawach. U owczarka prawidłowe kątowanie przednich (łopatka 45°, kąt łopatka-ramię ok. 90°) i tylnych kończyn zapewnia wydajny, dalekosięgny kłus. Zbyt strome lub zbyt głębokie kątowanie to wady." },
];

const AtlasHub = () => {
  return (
    <div>
      <KennelSEO
        title="Atlas Anatomii Owczarka Niemieckiego — interaktywny przewodnik | Wolfe vom Katzenberg"
        description="Interaktywny atlas anatomii owczarka niemieckiego. Budowa zewnętrzna, układ kostny, mięśnie, narządy, ruch i standard FCI. Przewodnik dla hodowców, właścicieli i miłośników rasy."
        canonical={BASE}
        ogImage={`${BASE}/german_shepherd_anatomy.png`}
        schema={[localBusinessSchema, faqSchema(hubFaq), breadcrumbSchema([
          { name: "Hodowla", href: "https://wolfevomkatzenberg.pl" },
          { name: "Atlas Anatomii", href: BASE }
        ])]}
      />

      {/* ── HERO ── */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.02]" style={{ backgroundImage: "repeating-linear-gradient(45deg, #D4AF37 0px, #D4AF37 1px, transparent 1px, transparent 50px)" }} />
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-[#D4AF37]/[0.03] rounded-full blur-[120px]" />

        <div className="max-w-5xl mx-auto px-4 md:px-8 pt-20 md:pt-32 pb-16 md:pb-24 relative">
          <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-5">
            INTERAKTYWNY PRZEWODNIK
          </motion.span>
          <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
            className="font-serif text-[clamp(2rem,5vw,3.8rem)] text-[#F5F5F5] leading-[1.1] mb-6 max-w-3xl" data-testid="atlas-h1"
          >
            Atlas Anatomii<br />
            <span className="text-[#D4AF37]">Owczarka Niemieckiego</span>
          </motion.h1>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.15 }} className="w-20 h-px bg-[#D4AF37]/40 mb-6" />
          <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}
            className="text-[#B8B8B8] text-sm md:text-base leading-relaxed max-w-2xl mb-4"
          >
            Poznaj szczegółową budowę owczarka niemieckiego warstwa po warstwie. Od sylwetki zewnętrznej, przez układ kostny i mięśniowy, po biomechanikę ruchu i kryteria oceny FCI.
          </motion.p>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.25 }}
            className="text-[#B8B8B8]/40 text-xs mb-10"
          >
            Dla hodowców, przyszłych właścicieli i miłośników rasy.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="flex flex-wrap gap-3">
            <Link to="/anatomia/owczarek-niemiecki/budowa-zewnetrzna" className="px-8 py-3 bg-[#D4AF37] text-[#0B0B0D] text-xs font-bold tracking-[0.15em] hover:bg-[#C8A96A] transition-all duration-300 flex items-center gap-2" data-testid="hub-cta-external">
              ZACZNIJ OD BUDOWY <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <Link to="/anatomia/owczarek-niemiecki/uklad-kostny" className="px-8 py-3 border border-[#D4AF37]/40 text-[#D4AF37] text-xs tracking-[0.15em] hover:bg-[#D4AF37] hover:text-[#0B0B0D] transition-all duration-300 flex items-center gap-2" data-testid="hub-cta-skeletal">
              UKŁAD KOSTNY <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ── LAYER CARDS ── */}
      <section className="px-4 md:px-8 pb-20 md:pb-28">
        <div className="max-w-5xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-10">
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-4">6 WARSTW</span>
            <h2 className="font-serif text-[clamp(1.4rem,3vw,2.2rem)] text-[#F5F5F5] mb-2" data-testid="atlas-h2-layers">
              Odkrywaj budowę warstwa po warstwie
            </h2>
            <p className="text-[#B8B8B8]/50 text-sm">Każda warstwa to osobna podstrona z interaktywnym modelem, opisami i znaczeniem hodowlanym.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {LAYERS.map((layer, idx) => {
              const Icon = layer.icon;
              const CardTag = layer.ready ? Link : "div";
              return (
                <motion.div key={layer.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ delay: idx * 0.07 }}
                >
                  <CardTag to={layer.ready ? layer.href : undefined}
                    className={`block relative overflow-hidden border border-[#2A2A2D] group transition-all duration-500 ${layer.ready ? "hover:border-[#D4AF37]/30 cursor-pointer" : "opacity-60"}`}
                    data-testid={`layer-card-${layer.id}`}
                  >
                    <div className="h-44 overflow-hidden">
                      <img src={layer.image} alt={layer.name}
                        className={`w-full h-full object-cover transition-all duration-700 ${layer.ready ? "opacity-30 group-hover:opacity-40 group-hover:scale-105" : "opacity-15"}`}
                      />
                    </div>
                    <div className="absolute inset-0 bg-gradient-to-t from-[#0B0B0D] via-[#0B0B0D]/70 to-transparent" />
                    <div className="absolute bottom-0 left-0 right-0 p-5">
                      <div className="flex items-center gap-2.5 mb-2.5">
                        <div className={`w-7 h-7 border flex items-center justify-center ${layer.ready ? "border-[#D4AF37]/40 group-hover:border-[#D4AF37]" : "border-[#2A2A2D]"} transition-colors`}>
                          {layer.ready ? <Icon className="w-3.5 h-3.5 text-[#D4AF37]" /> : <Lock className="w-3 h-3 text-[#B8B8B8]/20" />}
                        </div>
                        <h3 className={`text-sm font-medium ${layer.ready ? "text-[#F5F5F5] group-hover:text-[#D4AF37]" : "text-[#B8B8B8]/40"} transition-colors`}>{layer.name}</h3>
                      </div>
                      <p className="text-[#B8B8B8]/40 text-xs leading-relaxed">{layer.desc}</p>
                      {layer.ready ? (
                        <span className="text-[9px] tracking-[0.15em] text-[#D4AF37]/50 mt-3 block group-hover:text-[#D4AF37] transition-colors flex items-center gap-1">
                          EKSPLORUJ <ArrowRight className="w-3 h-3" />
                        </span>
                      ) : (
                        <span className="text-[9px] tracking-[0.15em] text-[#B8B8B8]/20 mt-3 block">WKRÓTCE</span>
                      )}
                    </div>
                  </CardTag>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── SEO: Why anatomy matters ── */}
      <section className="bg-[#141416] border-y border-[#2A2A2D] px-4 md:px-8 py-16 md:py-20">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-4">DLACZEGO WARTO</span>
            <h2 className="font-serif text-[clamp(1.4rem,3vw,2.2rem)] text-[#F5F5F5] mb-6">
              Dlaczego budowa psa ma znaczenie
            </h2>
            <div className="w-12 h-px bg-[#D4AF37]/30 mb-8" />
          </motion.div>
          <div className="grid md:grid-cols-2 gap-5">
            {[
              { t: "Wpływ na zdrowie", d: "Prawidłowa budowa zmniejsza ryzyko dysplazji stawów, problemów kręgosłupa i przedwczesnego zużycia chrząstek. Pies o dobrych proporcjach żyje dłużej i zdrowiej." },
              { t: "Wpływ na ruch", d: "Owczarek niemiecki to kłusak dalekiego zasięgu. Prawidłowe kątowanie, mocne lędźwie i odpowiedni kąt zadu pozwalają na wydajny ruch godzinami bez zmęczenia." },
              { t: "Wybór szczenięcia", d: "Znając prawidłową budowę, ocenisz rodziców miotu i przewidzisz, jak będzie wyglądał dorosły pies. To klucz do świadomego wyboru z odpowiedzialnej hodowli." },
              { t: "Ocena wystawowa", d: "Sędzia ocenia typ rasowy, proporcje, kątowanie, ruch i temperament. Znajomość standardu pozwala zrozumieć, na co patrzy sędzia i dlaczego Twój pies otrzymał taką ocenę." },
            ].map((item, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="bg-[#0B0B0D] border border-[#2A2A2D] p-6 group hover:border-[#D4AF37]/20 transition-all duration-500"
              >
                <h3 className="text-[#F5F5F5] text-sm font-medium mb-3 group-hover:text-[#D4AF37] transition-colors">{item.t}</h3>
                <p className="text-[#B8B8B8]/50 text-xs leading-relaxed group-hover:text-[#B8B8B8]/70 transition-colors">{item.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section className="px-4 md:px-8 py-16 md:py-20">
        <div className="max-w-4xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mb-10">
            <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-4">FAQ</span>
            <h2 className="font-serif text-[clamp(1.4rem,3vw,2.2rem)] text-[#F5F5F5] mb-4">Najczęściej zadawane pytania</h2>
            <div className="w-12 h-px bg-[#D4AF37]/30" />
          </motion.div>
          <div className="space-y-3" data-testid="atlas-faq">
            {hubFaq.map((item, idx) => (
              <HubFAQ key={idx} q={item.q} a={item.a} idx={idx} />
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="bg-[#141416] border-t border-[#2A2A2D] px-4 md:px-8 py-16 md:py-20">
        <div className="max-w-3xl mx-auto text-center">
          <span className="text-[10px] tracking-[0.3em] text-[#D4AF37] block mb-4">ZACZNIJ EKSPLORACJĘ</span>
          <h2 className="font-serif text-[clamp(1.4rem,3vw,2rem)] text-[#F5F5F5] mb-6">Wybierz warstwę i poznaj budowę rasy</h2>
          <div className="flex flex-wrap gap-3 justify-center">
            <Link to="/anatomia/owczarek-niemiecki/budowa-zewnetrzna" className="px-8 py-3 bg-[#D4AF37] text-[#0B0B0D] text-xs font-bold tracking-[0.15em] hover:bg-[#C8A96A] transition-all flex items-center gap-2">
              BUDOWA ZEWNĘTRZNA <ArrowRight className="w-3.5 h-3.5" />
            </Link>
            <Link to="/anatomia/owczarek-niemiecki/uklad-kostny" className="px-8 py-3 border border-[#D4AF37]/40 text-[#D4AF37] text-xs tracking-[0.15em] hover:bg-[#D4AF37] hover:text-[#0B0B0D] transition-all flex items-center gap-2">
              UKŁAD KOSTNY <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

/* ── FAQ Component ── */
import { useState } from "react";
const HubFAQ = ({ q, a, idx }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border border-[#2A2A2D] hover:border-[#D4AF37]/20 transition-colors" data-testid={`atlas-faq-${idx}`}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between p-5 text-left group">
        <span className="text-sm text-[#F5F5F5] pr-4 group-hover:text-[#D4AF37] transition-colors">{q}</span>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.2 }}>
          <Eye className="w-4 h-4 text-[#D4AF37] shrink-0" />
        </motion.div>
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} transition={{ duration: 0.3 }} className="overflow-hidden">
        <p className="px-5 pb-5 text-sm text-[#B8B8B8]/60 leading-relaxed">{a}</p>
      </motion.div>
    </div>
  );
};

export default AtlasHub;
